import axios from 'axios';
import baseConfig from './config';
export interface CheckIsMigratedArguments {
  tokenSource: string;
  remoteToken: string;
}
export default (context: any, config: Record<string, any> = {}) => {
  const getToken = (context: any) => {
    const token = context.state.remoteToken;
    if (!token) {
      const er = new Error('identityMigration: No remote token found.');
      context.dispatch('error', er, { root: true });
    }
    return token;
  };
  const setCookie = (cname: string, cvalue: string, exdays: number, path='/') => {
    const d = new Date();
    d.setTime(d.getTime() + (exdays*24*60*60*1000));
    const expires = 'expires='+ d.toUTCString();
    const domain = 'domain=.' + window.location.hostname;
    if (typeof path === undefined ) {
      path = '/';
    }
    document.cookie = cname + '=' + cvalue + ';path=' + path + ';' + domain + ';' + expires + ';path=/';
  };
  const initialRemoteToken = window.localStorage.getItem('remote-token');
  function createStore() {
    const store = {
      state: {
        userMigratedInfo: null,
        exchangeToken: null,
        remoteToken: initialRemoteToken,
        sso: baseConfig.sso,
        migrationResult: null,
        isMigrated: null,
      },
      actions: {
        async loginMigratedUser(context: any) {
          if (!context.state.exchangeToken) {
            const er = new Error('identityMigration:loginMigratedUser: No exchange token found.');
            context.dispatch('error', er, { root: true });
            return;
          }
          let result;
          try {
            result = await axios.get(config.venueService + baseConfig.userInfo, {
              headers: {
                'Authorization': `Token ${context.state.exchangeToken}`,
              },
            });
          } catch (err) {
            const er = new Error('identityMigration:loginMigratedUser: Error getting user info: ' + err);
            context.dispatch('error', er, { root: true });
            throw er;
          }
          let userId;
          try {
            userId = result.data.items[0].id.toString();
          } catch (ex) {
            //continue;
          }
          setCookie('src_token', context.state.exchangeToken, 15);
          setCookie('src_user_id', userId, 15);
          window.localStorage.setItem('userData', JSON.stringify(result.data.items[0]));
          context.commit('SET_MIGRATED_USER_INFO', result.data);
        },
        async checkIsMigrated(context: any) {
          const token = getToken(context);
          if (!token) { return; }
          let result;
          try {
            result = await axios.post(config.identityMigrationService + baseConfig.checkIsMigrated, {
              tokenSource: 'animelab',
            },{
              headers: {
                'Authorization': `${token}`,
              },
            });
          } catch (err) {
            const msg = err?.response?.data?.msg || 'Unknown Error.';
            const er = new Error('identityMigration:checkIsMigrated: Error checking if identity is migrated: ' + msg);
            context.dispatch('error', er, { root: true });
            throw er;
          }
          context.commit('SET_IS_MIGRATED', result.data.isMigrated);
        },
        async tokenExchange(context: any, payload: any = {}) {
          const token = payload.token || getToken(context);
          if (!token) { return; }
          let result;
          try {
            result = await axios.post(config.identityMigrationService + baseConfig.tokenExchange, {
              source: payload.sourceTokenFormat || 'animelab',
              target: payload.targetTokenFormat || 'funimation',
            },{
              headers: {
                'Authorization': `${token}`,
              },
            });
          } catch (err) {
            const msg = err?.response?.data?.msg || 'Unknown Error.';
            const er = new Error('identityMigration:tokenExchange: Error executing token exchange: ' + msg);
            context.dispatch('error', er, { root: true });
            throw er;
          }
          context.commit('SET_EXCHANGE_TOKEN', result.data.token);
        },
        async migrateAuto(context: any) {
          const token = getToken(context);
          if (!token) { return; }
          let result;
          try {
            result = await axios.post(config.identityMigrationService + baseConfig.migrateAuto, {
              tokenSource: 'animelab',
            },{
              headers: {
                'Authorization': `${token}`,
              },
            });
          } catch (err) {
            // this is not an error
            const msg = err?.response?.data?.msg || 'Unknown Error.';
            const migrationResult = err?.response?.data?.migrationResult;
            context.commit('SET_MIGRATION_RESULT', migrationResult);
            const er = new Error('identityMigration:migrateAuto: Error automatically migrating: ' + msg);
            if (migrationResult !== 'CONFLICTING_ACCOUNTS' || migrationResult !== 'INVALID_CREDENTIALS') {
              context.dispatch('error', er, { root: true });
            }
            throw er;
          }
          context.commit('SET_MIGRATION_RESULT', result.data.migrationResult);
        },
        async migrateLink(context: any) {
          const token = getToken(context);
          if (!token) { return; }
          if (!context.state.exchangeToken) {
            // https://funimation.atlassian.net/wiki/spaces/EN/pages/2264760374/Conflict+Link+Migration+Flow
            const er = new Error('identityMigration:migrateLink: Missing exchange token.  Run tokenExchange first.');
            context.dispatch('error', er, { root: true });
            throw er;
          }
          let result;
          try {
            result = await axios.post(config.identityMigrationService + baseConfig.migrateLink, {
              sourceToken: token,
              targetToken: context.state.exchangeToken,
              source: 'animelab',
              targetTokenFormat: 'funimation',
            });
          } catch (err) {
            const msg = err?.response?.data?.msg || 'Unknown Error.';
            const er = new Error('identityMigration:migrateLink: Error linking migrated accounts: ' + msg);
            context.dispatch('error', er, { root: true });
            throw er;
          }
          context.commit('SET_MIGRATION_RESULT', result.data.migrationResult);
        },
        setRemoteToken(context: any, payload: string) {
          window.localStorage.setItem('remote-token', payload);
          context.commit('SET_REMOTE_TOKEN', payload);
        },
      },
      mutations: {
        SET_MIGRATED_USER_INFO(state: any, payload: string) {
          state.userMigratedInfo = payload;
        },
        SET_MIGRATION_RESULT(state: any, payload: string) {
          state.migrationResult = payload;
        },
        SET_EXCHANGE_TOKEN(state: any, payload: string) {
          state.exchangeToken = payload;
        },
        SET_IS_MIGRATED(state: any, payload: string) {
          state.isMigrated = payload;
        },
        SET_REMOTE_TOKEN(state: any, payload: string) {
          state.remoteToken = payload;
        },
      },
    };
    context.registerModule('identityMigration', store);
  }
  return {
    install() {
      createStore();
    },
  };
};
